import {
  CloseButton,
  Button,
  RadioGroup,
  VStack,
  Text,
  Spinner,
  HStack,
  Dialog,
  Portal,
  type RadioGroupValueChangeDetails,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react'
import type { ScopeType, BoundingBox } from '../../types/analyticsMap'
import type { FilterGroupRequest } from '../../types/contactList'
import { useCountScope } from '../../hooks/useAnalyticsMap'
import { extractApiMessage } from '../../lib/apiError'
import { useReadCaps } from '../../hooks/useLimits'

interface ScopeConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (scope: ScopeType) => void
  hasHighValueProfiles: boolean
  hasPolygon: boolean
  /** When true, only polygon scope is shown (e.g., for Create Turf) */
  polygonOnly?: boolean
  title?: string
  // Pre-flight count props
  filterGroups?: FilterGroupRequest[]
  profileNames?: string[]
  polygonWkt?: string
  bBox?: BoundingBox
  thresholds?: Record<string, number>
}

export function ScopeConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  hasHighValueProfiles,
  hasPolygon,
  polygonOnly = false,
  title = 'Select Contact Scope',
  filterGroups,
  profileNames,
  polygonWkt,
  bBox,
  thresholds,
}: ScopeConfirmationModalProps) {
  const [scope, setScope] = useState<ScopeType>(
    polygonOnly ? 'polygon' : 'visible'
  )

  const countMutation = useCountScope()
  const hasCountData = filterGroups !== undefined

  const caps = useReadCaps()
  const previewCount = countMutation.data?.count
  // Pre-flight only: the server cap is the gate. Unknown caps => no warning, submit unchanged.
  const overScoringCap =
    caps !== undefined && previewCount !== undefined && previewCount > caps.maxScoringCandidates

  // Reset scope when modal opens
  useEffect(() => {
    if (isOpen) {
      setScope(polygonOnly ? 'polygon' : 'visible')
    }
  }, [isOpen, polygonOnly])

  // Fetch count when modal opens or scope changes
  useEffect(() => {
    if (isOpen && hasCountData) {
      countMutation.mutate({
        filterGroups: filterGroups ?? [],
        profileNames,
        polygonWkt,
        bBox,
        scope,
        thresholds,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, scope, hasCountData])

  const handleConfirm = () => {
    onConfirm(scope)
    onClose()
  }

  return (
    <Dialog.Root open={isOpen} size='sm' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header fontSize="md">{title}</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <Text fontSize="sm" color="gray.600" mb={4}>
                Which contacts should this action apply to?
              </Text>
              <RadioGroup.Root size="sm" value={scope} onValueChange={(details: RadioGroupValueChangeDetails) => setScope(details.value as ScopeType)}>
                <VStack align="start" gap={3}>
                  {!polygonOnly && (
                    <RadioGroup.Item value="visible"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>
                      <Text fontSize="sm">All visible contacts (current filters)</Text>
                    </RadioGroup.ItemText></RadioGroup.Item>
                  )}
                  {!polygonOnly && (
                    <RadioGroup.Item
                      value="highValue"
                      disabled={!hasHighValueProfiles}
                    ><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>
                      <Text fontSize="sm" color={hasHighValueProfiles ? undefined : 'gray.400'}>
                        Only high-value contacts (selected profiles)
                      </Text>
                    </RadioGroup.ItemText></RadioGroup.Item>
                  )}
                  <RadioGroup.Item value="polygon" disabled={!hasPolygon}><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>
                      <Text fontSize="sm" color={hasPolygon ? undefined : 'gray.400'}>
                        Contacts in drawn selection only
                      </Text>
                    </RadioGroup.ItemText></RadioGroup.Item>
                </VStack>
              </RadioGroup.Root>

              {/* Pre-flight count display */}
              {hasCountData && (
                <VStack mt={4} align="start" gap={1}>
                  {countMutation.isPending ? (
                    <HStack>
                      <Spinner size="xs" />
                      <Text fontSize="sm" color="gray.500">Counting contacts...</Text>
                    </HStack>
                  ) : countMutation.data !== undefined ? (
                    <>
                      <Text fontSize="sm" fontWeight="bold">
                        This will affect {countMutation.data.count} contacts
                      </Text>
                      {countMutation.data.count === 0 && (
                        <Text color="orange.500" fontSize="xs">
                          No contacts match this scope
                        </Text>
                      )}
                      {overScoringCap && (
                        <Text color="warning.fg" fontSize="xs">
                          This selection is {previewCount!.toLocaleString()} contacts; this server
                          scores at most {caps!.maxScoringCandidates.toLocaleString()} — narrow the
                          filter, or draw a smaller selection, or the action will be refused.
                        </Text>
                      )}
                    </>
                  ) : countMutation.error ? (
                    <Text fontSize="sm" color="danger.fg">
                      {extractApiMessage(countMutation.error, 'Could not count contacts for this scope.')}
                    </Text>
                  ) : null}
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button size="sm" variant="ghost" mr={2} onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorPalette="brand"
                onClick={handleConfirm}
                disabled={hasCountData && countMutation.isPending}
              >
                Continue
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
